import React, { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import ACTIONS from '../constants/Actions';
import Client from '../components/Client';
import CollaborativeEditor from '../components/CollaborativeEditor';
import RunnerPanel from '../components/CodeRunner/RunnerPanel';
import VoicePanel from '../components/VoiceChat/VoicePanel';
import ParticipantsPanel from '../components/ParticipantsPanel';
import { initSocket } from '../socket/socket';
import { projectsApi } from '../services/projects';
import LANGUAGES, { getMonacoLanguage } from '../constants/languages';
import {
  useLocation,
  useNavigate,
  Navigate,
  useParams,
} from 'react-router-dom';
import {
  MdContentCopy,
  MdExitToApp,
  MdFolderOpen,
  MdSave,
  MdTerminal,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdPerson,
  MdPlayArrow,
  MdPeople,
  MdVisibility,
  MdChat,
  MdAutoAwesome,
} from 'react-icons/md';
import ChatPanel from '../components/ChatPanel';
import AIMentorPanel from '../components/AI/AIMentorPanel';

const EditorPage = () => {
  const socketRef = useRef(null);
  const codeRef = useRef('');
  const location = useLocation();
  const { roomId } = useParams();
  const reactNavigator = useNavigate();
  const monacoEditorRef = useRef(null);

  const handleEditorMount = useCallback((editor, monaco) => {
    monacoEditorRef.current = editor;
  }, []);

  const getEditorContext = useCallback(() => {
    const editor = monacoEditorRef.current;
    if (!editor) return { code: codeRef.current, selectedCode: '', cursorLine: null };

    const selection = editor.getSelection();
    let selectedCode = '';
    if (selection && !selection.isEmpty()) {
      selectedCode = editor.getModel().getValueInRange(selection);
    }
    const position = editor.getPosition();
    return {
      code: codeRef.current,
      selectedCode,
      cursorLine: position?.lineNumber
    };
  }, []);

  // `participants` (line 85) is the single source of truth for room members.

  // Project state
  const [currentProjectId, setCurrentProjectId] = useState(
    location.state?.projectId || null
  );
  const [projectTitle, setProjectTitle] = useState('');
  const [projectLanguage, setProjectLanguage] = useState('javascript');
  const [customLanguage, setCustomLanguage] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const autoSaveRef = useRef(null);
  const [showConsole, setShowConsole] = useState(() => {
    const stored = sessionStorage.getItem('showConsole');
    return stored === null ? true : stored === 'true';
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [showAIMentor, setShowAIMentor] = useState(() => {
    const stored = sessionStorage.getItem('showAIMentor');
    return stored === null ? true : stored === 'true';
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const chatStateRef = useRef({ showChat: false });

  useEffect(() => {
    chatStateRef.current.showChat = showChat;
    if (showChat) {
      setUnreadCount(0);
    }
  }, [showChat]);

  // Resizing state
  
  useEffect(() => {
    sessionStorage.setItem('showConsole', showConsole);
  }, [showConsole]);

  useEffect(() => {
    sessionStorage.setItem('showAIMentor', showAIMentor);
  }, [showAIMentor]);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [consoleHeight, setConsoleHeight] = useState(280);
  const [mentorWidth, setMentorWidth] = useState(340);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [isResizingConsole, setIsResizingConsole] = useState(false);
  const [isResizingMentor, setIsResizingMentor] = useState(false);

  // Role & participants state
  const [myRole, setMyRole] = useState(null);           // 'owner' | 'editor' | 'viewer'
  const [participants, setParticipants] = useState([]); // [{ socketId, username, role, userId }]
  const [showParticipants, setShowParticipants] = useState(false);

  const username = location.state?.username;

  useEffect(() => {
    if (!username) return;

    const init = async () => {
      socketRef.current = await initSocket();

      socketRef.current.on('connect_error', handleErrors);
      socketRef.current.on('connect_failed', handleErrors);

      function handleErrors(err) {
        console.error('socket error:', err);
        toast.error('Socket connection failed, try again later.');
        reactNavigator('/');
      }

      socketRef.current.emit(ACTIONS.JOIN, {
        roomId,
        username,
      });

      socketRef.current.on(ACTIONS.JOINED, ({ username: joinedUsername }) => {
        if (joinedUsername !== username) {
          toast.success(`${joinedUsername} joined the room.`);
        }
        // State is updated via PARTICIPANTS_UPDATED (single source of truth)
      });

      socketRef.current.on(ACTIONS.DISCONNECTED, ({ socketId, username: leftUsername }) => {
        toast.success(`${leftUsername} left the room.`);
        // State is updated via PARTICIPANTS_UPDATED (single source of truth)
      });

      // ── Language sync: receive language changes from other users ──
      socketRef.current.on(ACTIONS.LANGUAGE_CHANGE, ({ language }) => {
        setProjectLanguage(language);
      });

      // ── Role & Participants ──

      // Receive own role + full participants list on join
      socketRef.current.on(ACTIONS.ROOM_ROLE, ({ role, participants: p }) => {
        setMyRole(role);
        setParticipants(p || []);
      });

      // Full participants list broadcast (after any join/leave/role change)
      socketRef.current.on(ACTIONS.PARTICIPANTS_UPDATED, (p) => {
        setParticipants(p || []);
      });

      // A specific user's role changed
      socketRef.current.on(ACTIONS.ROLE_UPDATED, ({ socketId: sid, newRole }) => {
        if (sid === socketRef.current?.id) {
          setMyRole(newRole);
          toast(`Your role changed to ${newRole}`, { icon: '🔄' });
        }
      });

      // Ownership transferred
      socketRef.current.on(ACTIONS.OWNERSHIP_TRANSFERRED, ({ newOwnerSocketId, prevOwnerSocketId, newOwnerUsername }) => {
        const myId = socketRef.current?.id;
        if (myId === newOwnerSocketId) {
          setMyRole('owner');
          toast.success('You are now the room owner! 👑');
        } else if (myId === prevOwnerSocketId) {
          setMyRole('editor');
        } else {
          toast(`${newOwnerUsername} is now the owner.`, { icon: '👑' });
        }
      });

      // This user has been kicked
      socketRef.current.on(ACTIONS.KICKED, ({ message }) => {
        toast.error(message || 'You have been removed from the room.');
        reactNavigator('/');
      });

      // Role-related server errors
      socketRef.current.on('role-error', ({ message }) => {
        toast.error(message);
      });

      // ── Chat Listeners ──
      socketRef.current.on(ACTIONS.CHAT_HISTORY, ({ messages }) => {
        setChatMessages(messages || []);
      });

      socketRef.current.on(ACTIONS.NEW_MESSAGE, (msg) => {
        setChatMessages((prev) => [...prev, msg]);
        
        const isOwn = msg.username === username;
        if (!isOwn && msg.type !== 'system') {
          if (!chatStateRef.current.showChat) {
            setUnreadCount(prev => prev + 1);
            toast.custom((t) => (
              <div 
                style={{ cursor: 'pointer', padding: '12px', background: 'var(--surface-light)', color: 'var(--text-primary)', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', border: '1px solid var(--border)' }}
                onClick={() => { setShowChat(true); toast.dismiss(t.id); }}
              >
                <strong style={{ display: 'block', marginBottom: '4px', color: 'var(--primary)' }}>{msg.username}</strong>
                <span style={{ fontSize: '13px' }}>
                  {msg.message.length > 50 ? msg.message.substring(0, 50) + '...' : msg.message}
                </span>
              </div>
            ), { duration: 5000, id: `chat-${msg.id}` });
          }
        }
      });

      socketRef.current.on(ACTIONS.USER_TYPING, ({ username: typingUsername }) => {
        setTypingUsers(prev => {
          if (!prev.includes(typingUsername)) return [...prev, typingUsername];
          return prev;
        });
      });

      socketRef.current.on(ACTIONS.USER_STOPPED_TYPING, ({ username: stoppedUsername }) => {
        if (stoppedUsername) {
          setTypingUsers(prev => prev.filter(u => u !== stoppedUsername));
        }
      });

      socketRef.current.on('chat-error', ({ message }) => {
        toast.error(message);
      });
    };

    init();

    return () => {
      socketRef.current?.disconnect();
      socketRef.current?.off(ACTIONS.JOINED);
      socketRef.current?.off(ACTIONS.DISCONNECTED);
      socketRef.current?.off(ACTIONS.LANGUAGE_CHANGE);
      socketRef.current?.off(ACTIONS.ROOM_ROLE);
      socketRef.current?.off(ACTIONS.PARTICIPANTS_UPDATED);
      socketRef.current?.off(ACTIONS.ROLE_UPDATED);
      socketRef.current?.off(ACTIONS.OWNERSHIP_TRANSFERRED);
      socketRef.current?.off(ACTIONS.KICKED);
      socketRef.current?.off('role-error');
      socketRef.current?.off(ACTIONS.CHAT_HISTORY);
      socketRef.current?.off(ACTIONS.NEW_MESSAGE);
      socketRef.current?.off(ACTIONS.USER_TYPING);
      socketRef.current?.off(ACTIONS.USER_STOPPED_TYPING);
      socketRef.current?.off('chat-error');
    };
  }, [roomId, username, reactNavigator]);

  // Load project if projectId was passed
  useEffect(() => {
    if (currentProjectId && location.state?.projectId) {
      loadProjectById(currentProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProjectById = async (id) => {
    try {
      const project = await projectsApi.getOne(id);
      setProjectTitle(project.title);
      setProjectLanguage(project.language);
      setCurrentProjectId(project._id);
      // The code will be set via the CollaborativeEditor's onCodeReady callback
      // For now, store it so the editor can pick it up
      codeRef.current = project.code || '';
      toast.success(`Loaded: ${project.title}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Auto-save effect
  useEffect(() => {
    if (autoSave && currentProjectId) {
      autoSaveRef.current = setInterval(async () => {
        try {
          await projectsApi.update(currentProjectId, {
            code: codeRef.current,
          });
          // Subtle indicator, no toast spam
          console.log('Auto-saved at', new Date().toLocaleTimeString());
        } catch (err) {
          console.error('Auto-save failed:', err);
        }
      }, 15000);
    }

    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
        autoSaveRef.current = null;
      }
    };
  }, [autoSave, currentProjectId]);

  // Callback from CollaborativeEditor when code changes
  const handleCodeChange = useCallback((code) => {
    codeRef.current = code;
  }, []);

  const handleLanguageChange = useCallback((newLang) => {
    setProjectLanguage(newLang);

    if (socketRef.current) {
      socketRef.current.emit(ACTIONS.LANGUAGE_CHANGE, {
        roomId,
        language: newLang,
      });
    }
  }, [roomId]);

  const copyRoomId = async () => {
    try {
      await navigator.clipboard.writeText(roomId);
      toast.success('Room ID copied to clipboard!');
    } catch {
      toast.error('Failed to copy Room ID');
    }
  };

  const leaveRoom = () => {
    setShowLeaveModal(true);
  };

  const confirmLeaveRoom = () => {
    reactNavigator('/');
  };

  const handleSendMessage = (message) => {
    if (socketRef.current) {
      const tempId = `temp-${Date.now()}`;
      socketRef.current.emit(ACTIONS.SEND_MESSAGE, {
        roomId,
        message,
        id: tempId
      });
    }
  };

  const handleTyping = (isTyping) => {
    if (socketRef.current) {
      if (isTyping) {
        socketRef.current.emit(ACTIONS.TYPING_START, { roomId });
      } else {
        socketRef.current.emit(ACTIONS.TYPING_STOP, { roomId });
      }
    }
  };

  // Save current code as new project
  const handleSaveAsNew = async () => {
    if (!projectTitle.trim()) {
      toast.error('Project title is required');
      return;
    }

    setSaving(true);
    const finalLanguage = projectLanguage === 'other' ? customLanguage : projectLanguage;

    if (projectLanguage === 'other' && !customLanguage.trim()) {
      toast.error('Please specify the language');
      setSaving(false);
      return;
    }

    try {
      const project = await projectsApi.create({
        title: projectTitle,
        language: finalLanguage,
        code: codeRef.current,
        roomId,
      });
      setCurrentProjectId(project._id);
      setShowSaveModal(false);
      setCustomLanguage(''); // Reset
      toast.success('Project saved!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Quick save (update existing project)
  const handleQuickSave = async () => {
    if (!currentProjectId) {
      setShowSaveModal(true);
      return;
    }

    setSaving(true);
    try {
      await projectsApi.update(currentProjectId, {
        code: codeRef.current,
        title: projectTitle,
        language: projectLanguage,
      });
      toast.success('Saved!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Open load modal
  const handleOpenLoadModal = async () => {
    setShowLoadModal(true);
    setLoadingProjects(true);
    try {
      const data = await projectsApi.getAll();
      setUserProjects(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Load a selected project
  const handleLoadProject = async (project) => {
    try {
      const full = await projectsApi.getOne(project._id);
      setCurrentProjectId(full._id);
      setProjectTitle(full.title);
      setProjectLanguage(full.language);
      codeRef.current = full.code || '';
      setShowLoadModal(false);
      toast.success(`Loaded: ${full.title}`);
      // We need to tell the editor to set this code
      // This is handled through the codeRef + a re-render trigger
      window.__loadedProjectCode = full.code || '';
      window.dispatchEvent(new Event('projectLoaded'));
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Trigger run from the sidebar button
  const triggerRun = () => {
    if (!showConsole) {
      setShowConsole(true);
    }
    // Give RunnerPanel a moment to mount if it wasn't already
    setTimeout(() => {
      window.dispatchEvent(new Event('triggerRun'));
    }, 100);
  };

  // Resizing handlers
  const startSidebarResize = useCallback((e) => {
    e.preventDefault();
    setIsResizingSidebar(true);
    const handleMouseMove = (e) => {
      let newWidth = e.clientX;
      if (newWidth < 200) newWidth = 200;
      if (newWidth > 600) newWidth = 600;
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => {
      setIsResizingSidebar(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const startConsoleResize = useCallback((e) => {
    e.preventDefault();
    setIsResizingConsole(true);
    
    // We need to define handleMouseUp before handleMouseMove so we can use it to cleanup
    let handleMouseMove, handleMouseUp;

    handleMouseUp = () => {
      setIsResizingConsole(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    handleMouseMove = (e) => {
      let newHeight = window.innerHeight - e.clientY;
      
      // Close if dragged to the very bottom (under 20px)
      if (newHeight < 20) {
        setShowConsole(false);
        handleMouseUp();
        return;
      }
      
      // Allow shrinking down to just the header height (around 40px)
      if (newHeight < 40) newHeight = 40;
      if (newHeight > window.innerHeight * 0.8) newHeight = window.innerHeight * 0.8;
      setConsoleHeight(newHeight);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const startMentorResize = useCallback((e) => {
    e.preventDefault();
    setIsResizingMentor(true);
    const handleMouseMove = (e) => {
      let newWidth = window.innerWidth - e.clientX;
      if (newWidth < 280) newWidth = 280;
      if (newWidth > window.innerWidth * 0.8) newWidth = window.innerWidth * 0.8;
      setMentorWidth(newWidth);
    };
    const handleMouseUp = () => {
      setIsResizingMentor(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  if (!location.state?.username) {
    return <Navigate to="/" />;
  }

  return (
    <div className="mainWrap" style={{ cursor: isResizingSidebar ? 'col-resize' : isResizingConsole ? 'row-resize' : 'default' }}>
      <div
        className={`aside ${!isSidebarOpen ? 'collapsed' : ''}`}
        style={isSidebarOpen ? { width: sidebarWidth, transition: isResizingSidebar ? 'none' : undefined } : {}}
      >
        <div className="asideInner">
          <div className="logo">
            <img className="logoImage" src="/code-sync.png" alt="logo" />
          </div>
          <div className="sidebarLanguageSelector">
            <span className="sidebarSectionTitle">Language</span>
            <div className="languageSelectorWrap" style={{ width: '100%', display: 'flex', gap: '10px' }}>
              <select
                className="languageSelector"
                value={projectLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                id="sidebar-language-selector"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.key} value={lang.key}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <button
                className="btn runBtn"
                onClick={triggerRun}
                title="Run Code"
              >
                <MdPlayArrow size={16} />
                Run
              </button>
            </div>
          </div>
          <div className="clientsList">
            {participants.map((p) => (
              <Client
                key={p.socketId}
                username={p.username}
              />
            ))}
          </div>
        </div>

        {/* Participants Panel (collapsible) */}
        {showParticipants && (
          <ParticipantsPanel
            participants={participants}
            mySocketId={socketRef.current?.id}
            myRole={myRole}
            onChangeRole={(targetSocketId, newRole) => {
              socketRef.current?.emit(ACTIONS.CHANGE_ROLE, { roomId, targetSocketId, newRole });
            }}
            onKickUser={(targetSocketId) => {
              socketRef.current?.emit(ACTIONS.KICK_USER, { roomId, targetSocketId });
            }}
            onTransferOwnership={(targetSocketId) => {
              socketRef.current?.emit(ACTIONS.TRANSFER_OWNERSHIP, { roomId, targetSocketId });
            }}
          />
        )}

        {/* Voice Chat Panel */}
        <VoicePanel
          roomId={roomId}
          username={location.state?.username}
          socketRef={socketRef}
        />

        {/* Compact Workspace Toolbar */}
        <div className="sidebarToolbar">
          <div className="toolbarGroup">
            <button className={`toolbarBtn ${showParticipants ? 'active' : ''}`} onClick={() => setShowParticipants(p => !p)} title="Team">
              <MdPeople size={18} />
            </button>
            <button className={`toolbarBtn ${showChat ? 'active' : ''}`} onClick={() => setShowChat(p => !p)} title="Chat" style={{ position: 'relative' }}>
              <MdChat size={18} />
              {unreadCount > 0 && !showChat && (
                <span className="toolbarBadge" />
              )}
            </button>
            <button className={`toolbarBtn ${showAIMentor ? 'active' : ''}`} onClick={() => setShowAIMentor(p => !p)} title="AI Mentor">
              <MdAutoAwesome size={18} />
            </button>
            <button className={`toolbarBtn ${showConsole ? 'active' : ''}`} onClick={() => setShowConsole(!showConsole)} title="Console">
              <MdTerminal size={18} />
            </button>
          </div>

          <div className="toolbarDivider" />

          <div className="toolbarGroup">
            <button className="toolbarBtn" onClick={handleQuickSave} title={currentProjectId ? 'Save' : 'Save As'}>
              <MdSave size={18} />
            </button>
            <button className="toolbarBtn" onClick={handleOpenLoadModal} title="Load Project">
              <MdFolderOpen size={18} />
            </button>
            <button className="toolbarBtn" onClick={copyRoomId} title="Copy Room ID">
              <MdContentCopy size={18} />
            </button>
          </div>

          <div className="toolbarDivider" />

          <button className="toolbarBtn leaveToolbarBtn" onClick={leaveRoom} title="Leave Room">
            <MdExitToApp size={18} />
          </button>
        </div>
      </div>

      {isSidebarOpen && (
        <div
          className="resizer-horizontal"
          onMouseDown={startSidebarResize}
        />
      )}

      <div className="editorWrap">
        {/* Hamburger Toggle */}
        <button
          className="sidebarToggleBtn"
          onClick={() => setIsSidebarOpen(prev => !prev)}
          title={isSidebarOpen ? "Hide Sidebar" : "Show Sidebar"}
        >
          {isSidebarOpen ? <MdKeyboardArrowLeft size={24} /> : <MdKeyboardArrowRight size={24} />}
        </button>

        {/* Viewer read-only notice */}
        {myRole === 'viewer' && (
          <div className="viewerReadonlyBar">
            <MdVisibility size={14} />
            <span>View only — editors can change your role</span>
          </div>
        )}

        <div className={`editorMain ${showConsole ? 'withConsole' : ''}`}>
          <CollaborativeEditor
            roomId={roomId}
            username={location.state.username}
            socketRef={socketRef}
            onCodeChange={handleCodeChange}
            language={getMonacoLanguage(projectLanguage)}
            readOnly={myRole === 'viewer'}
            onEditorMount={handleEditorMount}
          />
        </div>


        {showConsole && (
          <div
            className="resizer-vertical"
            onMouseDown={startConsoleResize}
          />
        )}

        {showConsole && (
          <RunnerPanel
            getCode={() => codeRef.current}
            language={projectLanguage}
            onLanguageChange={handleLanguageChange}
            height={consoleHeight}
            isResizing={isResizingConsole}
            onClose={() => setShowConsole(false)}
          />
        )}
      </div>

      {showChat && (
        <ChatPanel 
          messages={chatMessages}
          typingUsers={typingUsers}
          onSendMessage={handleSendMessage}
          onTyping={handleTyping}
          onClose={() => setShowChat(false)}
          currentUsername={location.state.username}
        />
      )}

      {showAIMentor && (
        <>
          <div
            className="resizer-horizontal"
            onMouseDown={startMentorResize}
          />
          <AIMentorPanel 
            onClose={() => setShowAIMentor(false)}
            getEditorContext={getEditorContext}
            language={projectLanguage}
            roomId={roomId}
            width={mentorWidth}
          />
        </>
      )}

      {/* Leave Modal */}
      {showLeaveModal && (
        <div className="modalOverlay" onClick={() => setShowLeaveModal(false)}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <h3>Leave Room</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}>
              Are you sure you want to leave this room?
            </p>
            <div className="modalActions">
              <button
                className="btn saveBtn"
                onClick={confirmLeaveRoom}
                style={{ backgroundColor: 'var(--danger)', borderColor: 'var(--danger)' }}
              >
                Leave
              </button>
              <button
                className="btn cancelBtn"
                onClick={() => setShowLeaveModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save As Modal */}
      {showSaveModal && (
        <div className="modalOverlay" onClick={() => setShowSaveModal(false)}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <h3>Save Project</h3>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <input
                type="text"
                className="inputBox"
                placeholder="Project Title"
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
                style={{ flex: 1, marginBottom: 0 }}
              />
              <select
                className="inputBox"
                value={projectLanguage}
                onChange={(e) => setProjectLanguage(e.target.value)}
                style={{ width: '150px', marginBottom: 0 }}
              >
                <option value="javascript">JavaScript</option>
                <option value="typescript">TypeScript</option>
                <option value="python">Python</option>
                <option value="java">Java</option>
                <option value="cpp">C++</option>
                <option value="c">C</option>
                <option value="html">HTML</option>
                <option value="css">CSS</option>
                <option value="json">JSON</option>
                <option value="other">Other</option>
              </select>
            </div>
            {projectLanguage === 'other' && (
              <input
                type="text"
                className="inputBox"
                placeholder="Specify Language"
                value={customLanguage}
                onChange={(e) => setCustomLanguage(e.target.value)}
              />
            )}
            <div className="modalActions">
              <button
                className="btn saveBtn"
                onClick={handleSaveAsNew}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                className="btn cancelBtn"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Modal */}
      {showLoadModal && (
        <div className="modalOverlay" onClick={() => setShowLoadModal(false)}>
          <div className="modalContent modalWide" onClick={(e) => e.stopPropagation()}>
            <h3>Load Project</h3>
            {loadingProjects ? (
              <div className="loadingWrapper" style={{ height: '100px' }}>
                <div className="spinner"></div>
              </div>
            ) : userProjects.length === 0 ? (
              <p style={{ color: '#aaa' }}>No saved projects yet.</p>
            ) : (
              <div className="loadProjectList">
                {userProjects.map((p) => (
                  <div
                    key={p._id}
                    className="loadProjectItem"
                    onClick={() => handleLoadProject(p)}
                  >
                    <span className="loadProjectTitle">{p.title}</span>
                    <span className="loadProjectLang">{p.language}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="modalActions">
              <button
                className="btn cancelBtn"
                onClick={() => setShowLoadModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorPage;
